import React, { useEffect, useRef, useState } from "react";
import { resolveColours } from "@/lib/playerColours";
import { botCharacter, myCharacter } from "@/lib/avatarConfig";
import { raiderFor } from "@/lib/raiderLooks";
import Avatar2D from "@/components/Avatar2D";
import LeaderCrown from "@/components/LeaderCrown";
import { playH2HSound } from "@/lib/h2hSound";

const SHOW_MS = 4600;
// The leader's crown hovers just above the raider's head (Avatar2D art).
const H2H_CROWN = { top: "11%", left: "52%" };

// The character for a match opponent: their chosen one, else their default.
// Bots get their own raider, else a default picked from their name.
export function fetchOpponentCharacter(opp) {
  if (!opp?.id || opp.isBot) return Promise.resolve(botCharacter(opp?.username));
  return raiderFor(opp.id);
}

// Pre-game "versus" screen: both raiders run in, square up, and the VS slams
// down. Tap to skip. Calls onDone when finished. Minotauro (Maze.jsx) passes
// its own opponent (oppCharacter + renderOpp, an SVG string for a pose) and
// its own labels. `crownCategory` ("jangle" / "race") floats the leader's
// crown over a raider who is that leaderboard's number 1, as on the board.
export default function HeadToHead({ user, me, opp, onDone, oppCharacter = null, sound = false, renderOpp = null,
  labels = null, colours = null, tagline = "Capture their flag · tap to start", crownCategory = null }) {
  const [oppChar, setOppChar] = useState(oppCharacter);
  const [phase, setPhase] = useState("in"); // in → vs → out
  const doneRef = useRef(false);
  const stopSound = useRef(null);
  const mine = myCharacter(user);
  // Each side in its player colour (Settings > My colour), named on its label.
  const cols = colours || resolveColours(user?.settings?.colour, "default");
  const meFill = cols.me.fill;
  const oppFill = cols.opp.fill;
  const baseLabels = labels || [`${cols.me.label} base`, `${cols.opp.label} base`];

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (stopSound.current) stopSound.current();
    setPhase("out");
    setTimeout(() => onDone?.(), 350);
  };

  useEffect(() => {
    if (oppCharacter) return undefined;
    let alive = true;
    fetchOpponentCharacter(opp).then((c) => alive && setOppChar(c));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opp?.id]);

  useEffect(() => {
    if (!oppChar) return undefined;
    const timers = [setTimeout(() => setPhase("vs"), 800), setTimeout(finish, SHOW_MS)];
    // Battle sound from the 3 November build, Stream only for now (Jack, 2026-11-04).
    if (sound) { try { if (localStorage.getItem("jangle:sound") !== "off") stopSound.current = playH2HSound(0.8); } catch { /* ignore */ } }
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oppChar]);

  const Name = ({ p, colour, align }) => (
    <div className={`flex flex-col ${align === "right" ? "items-end text-right" : "items-start"} gap-1 min-w-0`}>
      <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold" style={{ color: colour }}>
        {align === "right" ? baseLabels[1] : baseLabels[0]}
      </span>
      <span className="font-display font-black text-xl md:text-3xl tracking-tight text-white truncate max-w-[40vw]">{p.username || "Player"}</span>
      {p.elo != null && <span className="text-xs md:text-sm font-bold text-white/70 tabular-nums">{p.elo} Elo</span>}
    </div>
  );

  return (
    <div
      className={`fixed inset-0 z-[60] cursor-pointer select-none transition-opacity duration-300 ${phase === "out" ? "opacity-0" : "opacity-100"}`}
      onClick={finish}
      data-testid="head-to-head"
    >
      <style>{`
        @keyframes h2h-slam { 0% { transform: scale(3) rotate(-12deg); opacity: 0 } 60% { transform: scale(0.9) rotate(4deg); opacity: 1 } 100% { transform: scale(1) rotate(-4deg); opacity: 1 } }
        @keyframes h2h-flash { 0% { opacity: .9 } 100% { opacity: 0 } }
        @keyframes h2h-in-l { 0% { transform: translateX(-60vw) } 100% { transform: none } }
        @keyframes h2h-in-r { 0% { transform: translateX(60vw) } 100% { transform: none } }
        @keyframes h2h-rise { 0% { transform: translateY(24px); opacity: 0 } 100% { transform: none; opacity: 1 } }
      `}</style>
      <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, #3b1a2a 0%, #1b1420 49.6%, #121a2b 50.4%, #10233a 100%)" }} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 25% 60%, rgba(243,147,161,.35), transparent 55%), radial-gradient(ellipse at 75% 60%, rgba(79,176,240,.35), transparent 55%)" }} />
      {oppChar && (
        <div className="absolute inset-x-0 top-[22%] bottom-[22%] flex items-end justify-center gap-[4vw] pointer-events-none">
          {/* On narrow phones the raider's box is capped by width, so the
              drawing sits in an inner box of its own shape and the crown is
              placed against that (it floated far above heads, Jack 2026-11-01). */}
          <div className="h-full max-w-[44vw] aspect-[240/256] flex items-center" style={{ animation: "h2h-in-l .8s cubic-bezier(.2,.9,.3,1) both" }}>
            <div className="relative w-full aspect-[240/256]">
              <Avatar2D character={mine} pose={phase === "in" ? "walk" : "taunt"} flag="#e0457a" className="w-full h-full jav-glow" />
              <LeaderCrown userId={user?.id} category={crownCategory} className="raider-crown w-[48%]" style={H2H_CROWN} testId="h2h-crown-me" />
            </div>
          </div>
          <div className="h-full max-w-[44vw] aspect-[240/256] flex items-center" style={{ animation: "h2h-in-r .8s cubic-bezier(.2,.9,.3,1) both" }}>
            <div className="relative w-full aspect-[240/256]">
              {!opp?.isBot && <LeaderCrown userId={opp?.id} category={crownCategory} className="raider-crown w-[48%]" style={H2H_CROWN} testId="h2h-crown-opp" />}
              {renderOpp ? (
                <div className="w-full h-full" style={{ transform: "scaleX(-1)" }} dangerouslySetInnerHTML={{ __html: renderOpp(phase === "in" ? "walk" : "ready") }} />
              ) : (
                <Avatar2D character={oppChar} pose={phase === "in" ? "walk" : "ready"} flag="#1d74c9" mirror className="w-full h-full jav-glow" />
              )}
            </div>
          </div>
        </div>
      )}
      {phase !== "in" && (
        <>
          <div className="absolute inset-0 bg-white pointer-events-none" style={{ animation: "h2h-flash .35s ease-out forwards" }} />
          <div className="absolute inset-x-0 top-[12%] md:top-[14%] flex justify-center pointer-events-none">
            <span
              className="font-display font-black text-7xl md:text-9xl tracking-tighter text-white"
              style={{ animation: "h2h-slam .45s cubic-bezier(.2,1.4,.4,1) forwards", textShadow: `0 0 30px rgba(255,255,255,.35), 4px 4px 0 ${meFill}, -4px -4px 0 ${oppFill}` }}
            >
              VS
            </span>
          </div>
          <div className="absolute inset-x-0 bottom-[8%] px-5 md:px-16 flex justify-between items-end gap-4 pointer-events-none" style={{ animation: "h2h-rise .4s ease-out .1s both" }}>
            <Name p={me} colour={meFill} align="left" />
            <Name p={opp} colour={oppFill} align="right" />
          </div>
          <div className="absolute inset-x-0 bottom-3 text-center text-[11px] uppercase tracking-[0.3em] text-white/50 pointer-events-none">
            {tagline}
          </div>
        </>
      )}
    </div>
  );
}
